/**
 * @file functions for event tiles
 * @author tobias.frei@notch-interactive.com
 *
 * @module components/calendar/calendar-tile.js
 */

import model from './filter-model';
import helper from './filter-helper';
import getCoords from '../../helpers/getcoords';
import { scroll } from '../../helpers/smoothscroll';

const instance = {};

let scrollTimer;


/**
 * close all event tiles
 *
 * @return {void}
 */
instance.closeAllTiles = () => {
    let openTiles = [...document.querySelectorAll('.m-calendar .calendarTile.is-open')];

    openTiles.forEach((openTile) => {
        openTile.classList.remove('is-open');
        openTile.classList.remove('is-ready');
        openTile.style.height = 'auto';
    });
};


/**
 * click handler for event tile
 *
 * @param {object} e - trigger event object
 * @return {void}
 */
instance.triggerTile = (e) => {
    // Return if detail trigger
    if (e.target.closest('.calendarTile__detailcontainer')) {
        return false;
    }

    /**
     * Stop propagation of event. This line was added because of a change request, that required to make the whole tile
     * clickable. As there is already an anchor element inside the tile that also triggers this event-handler, we need
     * to make sure the event isn't fired twice when it bubbles up.
     */
    e.stopPropagation();

    let eventItem = e.currentTarget.closest('*[data-calendartile]');

    switch (eventItem.classList.contains('is-open')) {
        case true:
            instance.triggerClose({
                eventItem: eventItem
            });
            break;
        case false:
            instance.triggerOpen({
                eventItem: eventItem
            });
            break;
    }
};


/**
 * call open tile
 *
 * @param {object} eventItem - item to open
 * @return {void}
 */
instance.triggerOpen = ({ eventItem = {} } = {}) => {
    // Get open rows
    let rowToOpen = eventItem.getAttribute('data-row'),
        openRow = eventItem.parentNode.querySelector('.is-open[data-calendartile]'),
        scrollFlag = false;

    // If another tile is open
    if (openRow) {

        // Check for auto-scroll
        if (parseInt(rowToOpen) > parseInt(openRow.getAttribute('data-row'))) {
            scrollFlag = true;
        }

        // Remove open class on open tile
        openRow.classList.remove('is-open');

        // Close the open tile
        instance.closeTile({
            item: openRow,
            callback: true,
            callbackItemArr: [openRow],
            callbackFunc: [instance.closeTileDone],
            data: [
                {
                    id: openRow.getAttribute('data-id'),
                    item: openRow,
                    scroll: true
                }
            ]
        });

        // Auto-scroll during opening/closing
        if (scrollFlag) {
            let dif = openRow.offsetHeight - eventItem.offsetHeight,
                coordTop = getCoords(eventItem).top - dif,
                scrollOffset;

            coordTop > 180 ? scrollOffset = -85 : scrollOffset = -160;
            scroll(coordTop, {
                duration: model.settings.scrollDuration,
                easing: model.settings.scrollEasing,
                offset: scrollOffset
            });
        }

        // Open the new tile
        instance.openTile({
            item: eventItem,
            callback: true,
            callbackItemArr: [eventItem],
            callbackFunc: [instance.openTileDone],
            data: [
                {
                    id: openRow.getAttribute('data-id'),
                    item: openRow,
                    scroll: false
                }
            ]
        });


    } else { // If no other tile is open

        let viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0),
            viewportOffset = eventItem.getBoundingClientRect(),
            top = viewportOffset.top + eventItem.offsetHeight,
            delta = viewportHeight - top; // viewable pixels under the element

        // Check for auto-scroll
        if (delta < model.settings.scrollDelta) {
            let coordTop = getCoords(eventItem).top,
                scrollOffset;

            coordTop > 180 ? scrollOffset = -85 : scrollOffset = -160;
            scroll(coordTop, {
                duration: model.settings.scrollDuration,
                easing: model.settings.scrollEasing,
                offset: scrollOffset
            });
        }

        // Trigger open tile
        instance.openTile({
            item: eventItem,
            callback: true,
            callbackItemArr: [eventItem],
            callbackFunc: [instance.openTileDone],
            data: [
                {
                    id: eventItem.getAttribute('data-id'),
                    item: eventItem
                }
            ]
        });
    }

};


/**
 * call close tile
 *
 * @param {object} eventItem - item to close
 * @return {void}
 */
instance.triggerClose = ({ eventItem = eventItem }) => {
    instance.closeTile({
        item: eventItem,
        callback: true,
        callbackItemArr: [eventItem],
        callbackFunc: [instance.closeTileDone],
        data: [
            {
                id: eventItem.getAttribute('data-id'),
                item: eventItem
            }
        ]
    });
};


/**
 * open tile
 *
 * @param {object} item - item to open
 * @param {boolean=} callback - set transition callback
 * @param {object=} transitionItem - item to listen callback transition
 * @param {Array=} callbackItemArr - array of items to handle on callback
 * @param {Array=} callbackFunc - array of functions to fire on callback at callbackItemArr-Items
 * @param {object=} data - data for submit to callback as argument
 * @return {void}
 */
instance.openTile = ({ item = {}, callback = true, transitionItem = item, callbackItemArr = [item],  callbackFunc = [instance.openTileDone], data = {} } = {}) => {
    let tileHeight = item.offsetHeight,
        openHeight,
        detailHeight,
        detailElement = item.querySelector('.calendarTile__detailcontainer');

    if (callback) {
        transitionItem.args = [];

        callbackItemArr.forEach((cbItem, index) => {
            transitionItem.args.push(
                {
                    item: cbItem,
                    data: data[index]
                }
            );
        });

        transitionItem.callbackFunctions = callbackFunc;
        transitionItem.addEventListener('transitionend', helper.transitionListener, false);
    }

    item.classList.add('is-ready');
    item.setAttribute('data-open-val', tileHeight);

    detailHeight = detailElement.offsetHeight;
    openHeight = detailHeight + tileHeight;

    // item.style.zIndex = 0;
    item.style.height = tileHeight + 'px';
    instance.setTransition(true);

    setTimeout(function () {
        item.style.height = parseInt(openHeight) + 'px';

        if (!callback) {
            setTimeout(function () {
                model.calendar.$refs.cpt.layout(model.calendar.currentLayout.layoutMode);
            }, 100);
        }
    }, 100);
};


/**
 * close tile
 *
 * @param {object} item - item to close
 * @param {boolean=} callback - set transition callback
 * @param {object=} transitionItem - item to listen callback transition
 * @param {Array=} callbackItemArr - array of items to handle on callback
 * @param {Array=} callbackFunc - array of functions to fire on callback at callbackItemArr-Items
 * @param {object=} data - data for submit to callback as argument
 * @return {void}
 */
instance.closeTile = ({ item = {}, callback = true, transitionItem = item, callbackItemArr = [item],  callbackFunc = [instance.closeTileDone], data = {} } = {}) => {

    let openHeight;

    if (callback) {
        transitionItem.args = [];

        callbackItemArr.forEach((cbItem, index) => {
            transitionItem.args.push(
                {
                    item: cbItem,
                    data: data[index]
                }
            );
        });

        transitionItem.callbackFunctions = callbackFunc;
        transitionItem.addEventListener('transitionend', helper.transitionListener, false);
    }

    openHeight = item.getAttribute('data-open-val');
    instance.setTransition(true);

    // Set old height
    setTimeout(function () {
        item.style.height = parseInt(openHeight) + 'px';
    }, 100);
};


/**
 * open tile callback
 *
 * @param {object} item - callback item
 * @param {object=} data - callback data object
 * @param {boolean=} data.scroll - flag for auto-scroll
 * @return {void}
 */
instance.openTileDone = ({ item = {}, data = { scroll: false } } = {}) => {
    item.classList.add('is-open');

    // Add timeout for safari
    if (!data.scroll) {
        setTimeout(function () {
            model.calendar.$refs.cpt.layout(model.calendar.currentLayout.layoutMode);

            setTimeout(function () {
                instance.setTransition(false);
            }, 100);

        }, 100);
    }
};


/**
 * close tile callback
 *
 * @param {object} item - callback item
 * @param {object=} data - callback data object
 * @param {boolean=} data.scroll - flag for auto-scroll
 * @return {void}
 */
instance.closeTileDone = ({ item = {}, data = { scroll: false } } = {}) => {
    // Update styles
    item.classList.remove('is-open');
    item.classList.remove('is-ready');
    item.style.zIndex = 1;
    item.style.height = 'auto';

    // Wait for frames and call layout arrangement
    setTimeout(function () {
        model.calendar.$refs.cpt.layout(model.calendar.currentLayout.layoutMode);
    }, 100);

    // Set transition to slow if no auto-scroll
    if (!data.scroll) {
        setTimeout(function(){
            instance.setTransition(false);
        }, 100);
    }
};


/**
 * auto-scroll to tile
 *
 * @param {object} item - target item to scroll on position
 * @return {void}
 */
instance.scrollToTile = ({ item = {} } = {}) => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(function () {
        let coordTop = getCoords(item).top,
            scrollOffset;

        coordTop > 180 ? scrollOffset = -85 : scrollOffset = -160;
        scroll(coordTop, {
            duration: 200,
            easing: model.settings.scrollEasing,
            offset: scrollOffset
        });
    }, 400);
};


/**
 * switch masonry transition
 *
 * @param {boolean} fast - flag for fast transition
 * @return {void}
 */
instance.setTransition = (fast) => {
    if (model.calendar.currentLayout.layoutMode === 'vertical') {
        if (!fast) {
            model.calendar.$refs.cpt.iso.options.transitionDuration = model.settings.isotope.transitionDuration;
        } else {
            model.calendar.$refs.cpt.iso.options.transitionDuration = '10ms';
        }
    } else {
        if (!fast) {
            model.calendar.$refs.cpt.iso.options.transitionDuration = model.settings.isotope.transitionDuration;
        } else {
            model.calendar.$refs.cpt.iso.options.transitionDuration = '10ms';
        }
    }
};


/**
 * set isoptope stamp
 *
 * @param {object} e - event object
 * @return {void}
 */
instance.stamp = (e) => {
    var eventItem = e.currentTarget,
        tileHeight = eventItem.offsetHeight;

    stamp.style.height = parseInt(tileHeight) + 'px';
    stamp.style.top = parseInt(eventItem.offsetTop + tileHeight) + 'px';

    model.calendar.$refs.cpt.iso.stamp(stamp);
    model.calendar.$refs.cpt.iso.layout();
};


/**
 * set odd/even tile style
 *
 * @return {void}
 */
instance.setTileStyles = () => {
    let breakpoint = model.calendar.itemsOnRow,
        row = 0;

    model.calendar.$refs.cpt.iso.filteredItems.forEach((cloudItem, index) => {
        let cl = index % breakpoint,
            classes = cloudItem.element.getAttribute('class');

        if (cl === 0 && model.calendar.currentLayout.layoutMode !== 'vertical') {
            row++;
        } else if (model.calendar.currentLayout.layoutMode === 'vertical') {
            row++;
        }

        // Clean old classes with sort and break
        classes = classes.replace(/\bbreak-[0-9].*?\b/g, '');
        classes = classes.replace(/\bsort-[0-9].*?\b/g, '');
        cloudItem.element.setAttribute('class', classes);

        // Set new sort and break
        cloudItem.element.classList.add('sort-' + cl);
        cloudItem.element.classList.add('break-' + breakpoint);
        cloudItem.element.setAttribute('data-row', row);

        if (breakpoint % 2 === 0) {
            // Set odd/even classes switch row
            if (parseInt(cloudItem.element.getAttribute('data-row')) % 2) {
                if (index % 2 === 0) {
                    cloudItem.element.classList.add('calendarTile--odd');
                } else {
                    cloudItem.element.classList.remove('calendarTile--odd');
                }
            } else {
                if (index % 2 === 0) {
                    cloudItem.element.classList.remove('calendarTile--odd');
                } else {
                    cloudItem.element.classList.add('calendarTile--odd');
                }
            }
        } else {
            // Set odd/even classes
            if (index % 2 === 0) {
                cloudItem.element.classList.add('calendarTile--odd');
            } else {
                cloudItem.element.classList.remove('calendarTile--odd');
            }
        }

    });
};


export default instance;
